import { CalendarCheck, CreditCard, FileText } from '@phosphor-icons/react'
import { type Language } from './LandingPage'

const content = {
  pt: {
    title: 'Como o PinDay funciona',
    subtitle: 'Fluxo factual do produto: proposta, cobrança e bloqueio da data.',
    steps: [
      { icon: FileText, number: '1', title: 'Monte a proposta', description: 'Organize pacote, termos, política de cancelamento e contexto comercial da data.' },
      { icon: CreditCard, number: '2', title: 'Escolha o tipo de cobrança', description: 'One-off para sinal e saldo posterior. Recorrente para assinatura via Stripe em Pro e Studio.' },
      { icon: CalendarCheck, number: '3', title: 'Confirmação e bloqueio', description: 'Quando o pagamento confirma, a data entra no calendário com hold coerente e regras ativas.' },
    ],
  },
  en: {
    title: 'How PinDay works',
    subtitle: 'The factual product flow: proposal, billing mode, and date blocking.',
    steps: [
      { icon: FileText, number: '1', title: 'Build the proposal', description: 'Set package, terms, cancellation policy, and the commercial context of the date.' },
      { icon: CreditCard, number: '2', title: 'Choose the billing mode', description: 'One-off for deposit and later balance. Recurring for Stripe subscriptions on Pro and Studio.' },
      { icon: CalendarCheck, number: '3', title: 'Confirmation and blocking', description: 'When payment confirms, the date goes into the calendar with the right hold and active rules.' },
    ],
  },
  es: {
    title: 'Cómo funciona PinDay',
    subtitle: 'Flujo factual del producto: propuesta, tipo de cobro y bloqueo de la fecha.',
    steps: [
      { icon: FileText, number: '1', title: 'Monta la propuesta', description: 'Organiza paquete, términos, política de cancelación y contexto comercial de la fecha.' },
      { icon: CreditCard, number: '2', title: 'Elige el tipo de cobro', description: 'One-off para seña y saldo posterior. Recurrente para suscripción Stripe en Pro y Studio.' },
      { icon: CalendarCheck, number: '3', title: 'Confirmación y bloqueo', description: 'Cuando el pago se confirma, la fecha entra al calendario con el hold correcto y reglas activas.' },
    ],
  },
}

interface HowItWorksProps {
  language: Language
}

export function HowItWorks({ language }: HowItWorksProps) {
  const t = content[language]

  return (
    <section id="how-it-works" className="bg-[#fffaf3] px-6 py-20">
      <div className="mx-auto max-w-7xl">
        <div className="max-w-3xl">
          <h2 className="text-4xl font-semibold tracking-tight text-[#0b2a4a] sm:text-5xl">{t.title}</h2>
          <p className="mt-4 text-lg text-[#0b2a4a]/72">{t.subtitle}</p>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {t.steps.map((step) => {
            const Icon = step.icon
            return (
              <article key={step.number} className="rounded-[30px] border border-[#0b2a4a]/10 bg-white p-8 shadow-[0_18px_56px_rgba(11,42,74,0.06)]">
                <div className="flex items-center justify-between">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0b2a4a] text-[#f6ebdd]">
                    <Icon size={28} />
                  </div>
                  <span className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0b2a4a]/35">{step.number}</span>
                </div>
                <h3 className="mt-8 text-2xl font-semibold text-[#0b2a4a]">{step.title}</h3>
                <p className="mt-4 text-base leading-7 text-[#0b2a4a]/72">{step.description}</p>
              </article>
            )
          })}
        </div>
      </div>
    </section>
  )
}
